import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { AlertCircle, TrendingUp, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CopyrightFooter } from '../components/CopyrightFooter';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, verify2FA, requires2FA } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      if (!result.requires2FA) {
        toast.success('Accesso effettuato');
        navigate('/portal');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (otpCode.length !== 6) return;
    setLoading(true);
    setError('');

    try {
      await verify2FA(otpCode);
      toast.success('Accesso effettuato');
      navigate('/portal');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice non valido');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8 flex-1 flex flex-col justify-center">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/86bit_logo.svg" alt="86BIT" className="h-8 w-auto" />
              <span className="text-2xl font-bold tracking-tight">86BIT Finance</span>
            </div>
            <p className="text-muted-foreground">
              Monitoraggio contabile intelligente
            </p>
          </div>

          {!requires2FA ? (
            <Card className="border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl">Accedi</CardTitle>
                <CardDescription>Inserisci le tue credenziali</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
                      <AlertCircle className="h-4 w-4" />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="nome@azienda.it"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Accesso in corso...
                      </>
                    ) : (
                      'Accedi'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <CardTitle className="text-xl">Verifica 2FA</CardTitle>
                </div>
                <CardDescription>
                  Inserisci il codice a 6 cifre dall'app authenticator
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="flex items-center gap-2 p-3 mb-4 bg-destructive/10 text-destructive rounded-md text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}

                <div className="flex flex-col items-center space-y-6">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={setOtpCode}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>

                  <Button
                    onClick={handleVerify2FA}
                    disabled={loading || otpCode.length !== 6}
                    className="w-full"
                    data-testid="verify-2fa-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifica in corso...
                      </>
                    ) : (
                      'Verifica'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        <CopyrightFooter />
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 login-bg" />
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h2 className="text-4xl font-bold mb-4">
            Controllo totale sulla tua azienda
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Dashboard completa con grafici, KPI, proiezioni AI e alert automatici per monitorare l'andamento aziendale.
          </p>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <TrendingUp className="h-5 w-5" />
              </div>
              <span className="text-sm">Grafici interattivi</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Shield className="h-5 w-5" />
              </div>
              <span className="text-sm">Sicurezza 2FA</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
